import { Box, Card, CardActionArea, CardContent, Chip, Skeleton, Stack, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as OfflineIcon,
  Warning as UnregisteredIcon,
} from '@mui/icons-material';
import { PRODUCT_NAMES } from '@cnapse/shared';
import type { DeviceInventoryStatus } from '@cnapse/shared/schemas.js';
import * as dt from '@cnapse/shared/data.types.js';

export interface EfemGatewayEntry {
  sn: string;
  name: string;
  status: dt.ONLINE_OFFLINE_STATUS;
  inventoryStatus: DeviceInventoryStatus;
  /** False when a device inventory record exists but no EfemClient record yet */
  isRegistered: boolean;
}

interface EfemGatewayListProps {
  gateways: EfemGatewayEntry[];
  selectedSn: string | null;
  onSelect: (sn: string) => void;
  loading: boolean;
}

export function EfemGatewayList({ gateways, selectedSn, onSelect, loading }: EfemGatewayListProps) {
  if (loading) {
    return (
      <Stack spacing={1} p={1}>
        {[1, 2, 3].map((k) => (
          <Skeleton key={k} variant="rounded" height={72} />
        ))}
      </Stack>
    );
  }

  if (gateways.length === 0) {
    return (
      <Box p={2} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          No {PRODUCT_NAMES.AXON} gateways registered for this tenant.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={0.5} p={1}>
      {gateways.map((gateway) => {
        const isSelected = gateway.sn === selectedSn;
        const isOnline = gateway.status === dt.ONLINE_OFFLINE_STATUS.ONLINE;

        return (
          <Card
            key={gateway.sn}
            variant="outlined"
            sx={{
              borderWidth: isSelected ? 2 : 1,
              borderColor: isSelected ? 'primary.main' : 'divider',
              bgcolor: isSelected ? 'action.selected' : 'transparent',
              backgroundImage: 'none',
              boxShadow: isSelected ? 1 : 0,
              transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
              '&:hover': {
                borderColor: isSelected ? 'primary.main' : 'primary.light',
                bgcolor: isSelected ? 'action.selected' : 'action.hover',
              },
            }}
          >
            <CardActionArea onClick={() => onSelect(gateway.sn)} sx={{ px: 1.5, py: 1 }}>
              <CardContent sx={{ p: '0 !important' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" fontWeight="bold" noWrap sx={{ flex: 1 }}>
                    {gateway.name || gateway.sn}
                  </Typography>
                  {gateway.isRegistered ? (
                    <Chip
                      icon={isOnline ? <CheckCircleIcon sx={{ fontSize: 12 }} /> : <OfflineIcon sx={{ fontSize: 12 }} />}
                      label={isOnline ? 'Online' : 'Offline'}
                      size="small"
                      color={isOnline ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ ml: 1, height: 20, fontSize: 10 }}
                    />
                  ) : gateway.inventoryStatus !== 'active' ? (
                    <Chip
                      icon={<UnregisteredIcon sx={{ fontSize: 12 }} />}
                      label="Awaiting activation"
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ ml: 1, height: 20, fontSize: 10 }}
                    />
                  ) : (
                    <Chip
                      icon={<UnregisteredIcon sx={{ fontSize: 12 }} />}
                      label="Not initialized"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ ml: 1, height: 20, fontSize: 10 }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace" noWrap>
                  {gateway.sn}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Stack>
  );
}
